import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STAGE_CONFIGS, STAGE_ORDER } from '../../data/stageConfigs';
import type { StageConfig, StageQuestion } from '../../data/stageConfigs';
import type { StageName } from '../../lib/collections';
import type {
  ProblemDiaryEntry,
  FriendSurveyResponse,
  WouldYouRatherItem,
} from '../../types/businessPlan';

export type { StageQuestion } from '../../data/stageConfigs';
export type StageStatus = 'done' | 'active' | 'locked';

export interface DarkStage {
  key: StageName;
  id: number;
  name: string;
  emoji: string;
  blurb: string;
  status: StageStatus;
  questions: StageQuestion[];
}

/** One bag of homework state shared across all stages, mirroring the
 *  BusinessPlan shape so the existing homework components plug in unchanged. */
export interface HomeworkState {
  // Stage 1 — Logo Builder
  logoKeywords: string;
  selectedLogoColors: string[];
  selectedLogoShape: string;
  // Stage 2 — Problem Diary
  problemDiaryEntries: ProblemDiaryEntry[];
  // Stage 3 — CEO Contract
  ceoBigPromise: string;
  ceoCustomerFeeling: string;
  ceoCeoOath: string;
  // Stage 4 — Friend Survey
  friendSurveyResponses: FriendSurveyResponse[];
  // Stage 5 — Would You Rather + profit calc
  wouldYouRatherItems: WouldYouRatherItem[];
  profitCalculation: { costToMake: number; sellingPrice: number };
  // Stage 6 — Elevator Pitch
  elevatorPitchWho: string;
  elevatorPitchProblem: string;
  elevatorPitchSolution: string;
  elevatorPitchWhy: string;
}

const EMPTY_HOMEWORK: HomeworkState = {
  logoKeywords: '',
  selectedLogoColors: [],
  selectedLogoShape: '',
  problemDiaryEntries: [],
  ceoBigPromise: '',
  ceoCustomerFeeling: '',
  ceoCeoOath: '',
  friendSurveyResponses: [],
  wouldYouRatherItems: [],
  profitCalculation: { costToMake: 0, sellingPrice: 0 },
  elevatorPitchWho: '',
  elevatorPitchProblem: '',
  elevatorPitchSolution: '',
  elevatorPitchWhy: '',
};

interface AiCacheEntry {
  inputHash: string;
  text: string;
}

interface DarkState {
  explorerName: string;
  avatarEmoji: string;
  xp: number;
  xpMax: number;
  streak: number;
  stages: DarkStage[];
  /** answers[stageKey][questionId] = value (string) */
  answers: Record<StageName, Record<string, string>>;
  homework: HomeworkState;
  activeStageId: number;
  /** Cached AI feedback per stage — keyed by stage, invalidated when answers change */
  feedbackCache: Partial<Record<StageName, AiCacheEntry>>;
  /** Cached AI summary for the final plan — invalidated when any answers change */
  summaryCache: AiCacheEntry | null;
  setExplorer: (name: string, avatar: string) => void;
  setAnswer: (stageKey: StageName, questionId: string, value: string) => void;
  setHomework: <K extends keyof HomeworkState>(
    key: K,
    value: HomeworkState[K],
  ) => void;
  addXp: (n: number) => void;
  completeStage: (id: number) => void;
  setFeedbackCache: (stageKey: StageName, inputHash: string, text: string) => void;
  setSummaryCache: (inputHash: string, text: string) => void;
  resetPlan: () => void;
}

function stagesFromConfig(): DarkStage[] {
  return STAGE_ORDER.map((key, idx) => {
    const cfg: StageConfig = STAGE_CONFIGS[key];
    const status: StageStatus = idx === 0 ? 'active' : 'locked';
    return {
      key,
      id: cfg.number,
      name: cfg.name,
      emoji: cfg.emoji,
      blurb: cfg.worldlyInsight.title,
      status,
      questions: cfg.questions,
    };
  });
}

const emptyAnswers = () =>
  STAGE_ORDER.reduce(
    (acc, k) => {
      acc[k] = {};
      return acc;
    },
    {} as Record<StageName, Record<string, string>>,
  );

export const useDarkStore = create<DarkState>()(
  persist(
    (set) => ({
      explorerName: '',
      avatarEmoji: '🦊',
      xp: 0,
      xpMax: 500,
      streak: 0,
      stages: stagesFromConfig(),
      answers: emptyAnswers(),
      homework: EMPTY_HOMEWORK,
      activeStageId: 1,
      feedbackCache: {},
      summaryCache: null,
      setExplorer: (name, avatar) =>
        set({ explorerName: name, avatarEmoji: avatar }),
      setAnswer: (stageKey, questionId, value) =>
        set((s) => ({
          answers: {
            ...s.answers,
            [stageKey]: { ...s.answers[stageKey], [questionId]: value },
          },
        })),
      setHomework: (key, value) =>
        set((s) => ({ homework: { ...s.homework, [key]: value } })),
      addXp: (n) => set((s) => ({ xp: Math.min(s.xpMax, s.xp + n) })),
      setFeedbackCache: (stageKey, inputHash, text) =>
        set((s) => ({
          feedbackCache: { ...s.feedbackCache, [stageKey]: { inputHash, text } },
        })),
      setSummaryCache: (inputHash, text) =>
        set({ summaryCache: { inputHash, text } }),
      completeStage: (id) =>
        set((s) => {
          const stages = s.stages.map((st) =>
            st.id === id ? { ...st, status: 'done' as StageStatus } : st,
          );
          const nextIdx = stages.findIndex((st) => st.id === id + 1);
          if (nextIdx >= 0 && stages[nextIdx].status === 'locked') {
            stages[nextIdx] = { ...stages[nextIdx], status: 'active' };
          }
          return { stages, activeStageId: id + 1 };
        }),
      resetPlan: () =>
        set({
          explorerName: '',
          avatarEmoji: '🦊',
          xp: 0,
          xpMax: 500,
          streak: 0,
          stages: stagesFromConfig(),
          answers: emptyAnswers(),
          homework: EMPTY_HOMEWORK,
          activeStageId: 1,
          feedbackCache: {},
          summaryCache: null,
        }),
    }),
    {
      name: 'omore-dark-plan',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
